import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ArrowLeft, IdCard, LifeBuoy, Briefcase, FileText, AlertTriangle,
  Key, Plus, Archive, Banknote, Pencil, Printer, Flag, PhoneCall,
  type LucideIcon,
} from 'lucide-react';
import { toast } from '../store/toast.js';
import { useUiStore } from '../store/ui.js';
import { useUnsavedGuard } from '../lib/unsaved.js';
import {
  useMe,
  useEmployeesFull,
  useEmployeeSalaries,
  useEntities,
  useUpdateEmployee,
  useCreateEmployee,
  useDeactivateEmployee,
  useAdjustEmployeeBalance,
  usePostes,
  useEmployeePostes,
  useAddEmployeePoste,
  type EmployeeFullDTO,
  type EntityDTO,
  type EmployeePatch,
  type EmployeePosteDTO,
  type LeaveType,
} from '../api/hooks.js';
import { fmtDA } from '../lib/format.js';
import { PAYMENT_METHODS, PAYMENT_METHOD_KEYS, type PaymentMethod } from '@pointage/shared';
import '../styles/fiche-employe.css';

const fr1 = (n: number) => n.toFixed(1).replace('.', ',');
const initials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
const CIVILITIES = ['', 'M.', 'Mme', 'Melle'];

// ─── date / tenure helpers (ported from the proto) ───────────────
function isoDay(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}
function frDate(iso: string | null | undefined): string {
  const d = isoDay(iso);
  return d ? d.split('-').reverse().join('/') : '';
}
function monthsBetween(a: string, b: string): number | null {
  if (!a) return null;
  const d1 = new Date(`${a}T00:00:00`), d2 = b ? new Date(`${b}T00:00:00`) : new Date();
  let m = (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
  if (d2.getDate() < d1.getDate()) m -= 1;
  return Math.max(0, m);
}
function fmtTenure(m: number | null): string {
  if (m == null) return '—';
  const y = Math.floor(m / 12), mm = m % 12;
  const parts: string[] = [];
  if (y) parts.push(y + ' an' + (y > 1 ? 's' : ''));
  if (mm) parts.push(mm + ' mois');
  return parts.length ? parts.join(' ') : '< 1 mois';
}
function addMonths(iso: string, n: number): string {
  if (!iso || !n) return '';
  // Operate on calendar components and format back from local fields —
  // never round-trip through toISOString(), which shifts the day in
  // any non-UTC timezone (probation end was landing a day early).
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m - 1) + n, d);
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
const TODAY = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
function daysFromToday(iso: string): number {
  return Math.round((new Date(`${iso}T00:00:00`).getTime() - TODAY.getTime()) / 86400000);
}
function relFr(days: number): string {
  const a = Math.abs(days);
  let txt: string;
  if (a === 0) return 'aujourd’hui';
  else if (a < 31) txt = a + ' jour' + (a > 1 ? 's' : '');
  else if (a < 365) txt = Math.round(a / 30.4) + ' mois';
  else { const y = Math.floor(a / 365), mo = Math.round((a % 365) / 30.4); txt = y + ' an' + (y > 1 ? 's' : '') + (mo ? ' ' + mo + ' mois' : ''); }
  return days < 0 ? 'il y a ' + txt : 'dans ' + txt;
}
const Mono = ({ children }: { children: React.ReactNode }) => <span className="mono">{children}</span>;

// A date de sortie that has arrived means the employee is gone from every
// module, whether or not the fiche was also archived. This registry is the
// one page that still lists them (it is where a departure is entered and
// corrected), so it labels them rather than hiding them.
const TODAY_ISO = (() => {
  const d = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
})();
function hasLeft(e: { dateSortie: string | null }): boolean {
  return e.dateSortie != null && e.dateSortie.slice(0, 10) <= TODAY_ISO;
}
function isCurrent(e: { active: boolean; dateSortie: string | null }): boolean {
  return e.active && !hasLeft(e);
}

function entityMap(entities: EntityDTO[] | undefined) {
  return new Map((entities ?? []).map((e) => [e.id, e]));
}

// View state: the list, or a full-page detail. `detailId === null` is
// the "create" case (same page, edit mode, blank record).
type ViewState =
  | { kind: 'list' }
  | { kind: 'detail'; id: string | null; startEdit: boolean };

export function EmployeesPage() {
  const employees = useEmployeesFull();
  const entities = useEntities();
  const entityFilter = useUiStore((s) => s.currentEntityId);
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  // ?emp=<id> opens a fiche straight away — how the Organigramme's
  // « Voir la fiche » lands here. Consumed once, then dropped from the
  // URL so Retour goes back to the list rather than re-opening it.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLink = searchParams.get('emp');
  const [view, setView] = useState<ViewState>(
    deepLink ? { kind: 'detail', id: deepLink, startEdit: false } : { kind: 'list' },
  );
  useEffect(() => {
    if (!deepLink) return;
    const p = new URLSearchParams(searchParams);
    p.delete('emp');
    setSearchParams(p, { replace: true });
    setView({ kind: 'detail', id: deepLink, startEdit: false });
  }, [deepLink, searchParams, setSearchParams]);

  const eMap = useMemo(() => entityMap(entities.data), [entities.data]);
  const all = employees.data ?? [];
  const activeTotal = all.filter(isCurrent).length;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((e) => (activeOnly ? isCurrent(e) : true))
      .filter((e) => (entityFilter === 'all' ? true : e.entityId === entityFilter))
      .filter((e) =>
        q === ''
          ? true
          : [e.name, e.code, e.email ?? '', e.department ?? '']
              .some((f) => f.toLowerCase().includes(q)),
      );
  }, [all, query, activeOnly, entityFilter]);

  const defaultEntityId = entityFilter !== 'all' ? entityFilter : (entities.data?.[0]?.id ?? '');

  if (view.kind === 'detail') {
    return (
      <EmployeeDetailPage
        key={view.id ?? 'new'}
        employeeId={view.id}
        startEdit={view.startEdit}
        entities={entities.data ?? []}
        defaultEntityId={defaultEntityId}
        onBack={() => setView({ kind: 'list' })}
        onCreated={(id) => setView({ kind: 'detail', id, startEdit: false })}
      />
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="page-eyebrow">Annuaire · {entities.data?.length ?? 4} entités</div>
        <h1 className="page-title">Employés <em>du groupe</em></h1>
        <p className="page-subtitle">
          {activeTotal} employés actifs. Cliquez sur un nom pour ouvrir la fiche en lecture, puis
          « Modifier » pour éditer.
        </p>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search">
          <input
            placeholder="Rechercher par nom, poste, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="emp-search"
            aria-label="Rechercher un employé"
          />
        </div>
        <button
          className="btn btn-sm"
          onClick={() => setActiveOnly((v) => !v)}
          data-testid="emp-status-toggle"
        >
          Statut · {activeOnly ? 'Actifs' : 'Tous'}
        </button>
        <div className="toolbar-spacer" />
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setView({ kind: 'detail', id: null, startEdit: true })}
          data-testid="emp-add"
        >
          + Ajouter un employé
        </button>
      </div>

      {employees.isLoading && <p className="page-placeholder">Chargement…</p>}
      {employees.isError && (
        <p className="page-placeholder" data-testid="emp-error">Impossible de charger les employés.</p>
      )}

      {employees.data && (
        <div className="card" style={{ padding: 0 }} data-testid="emp-table">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: '28%' }}>Employé</th>
                <th style={{ width: '18%' }}>Entité</th>
                <th style={{ width: '22%' }}>Email</th>
                <th className="num" style={{ width: '11%' }}>Embauché</th>
                <th className="num" style={{ width: '9%' }}>Congé</th>
                <th className="num" style={{ width: '8%' }}>Récup</th>
                <th style={{ width: '8%' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const ent = eMap.get(e.entityId);
                return (
                  <tr
                    key={e.id}
                    onClick={() => setView({ kind: 'detail', id: e.id, startEdit: false })}
                    style={{ cursor: 'pointer' }}
                    data-testid={`emp-row-${e.id}`}
                  >
                    <td>
                      <div className="empcell">
                        <div className="empcell-avatar" style={{ background: ent?.color ?? 'var(--shf)' }}>
                          {initials(e.name)}
                        </div>
                        <div>
                          <div className="empcell-name">{e.name}</div>
                          <div className="empcell-meta">{e.department ?? e.code}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {ent && (
                        <span className={`pill ${ent.short.toLowerCase()}`}>{ent.short}</span>
                      )}
                    </td>
                    <td><span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{e.email ?? '—'}</span></td>
                    <td className="num">{e.hiredAt ?? '—'}</td>
                    <td className="num">{fr1(e.congeBalance)}</td>
                    <td className="num">{fr1(e.recupBalance)}</td>
                    <td>
                      {hasLeft(e)
                        ? <span className="pill" style={{ opacity: 0.6 }} title={`Date de sortie · ${e.dateSortie}`}>Sorti</span>
                        : e.active
                          ? <span className="pill ok">Actif</span>
                          : <span className="pill" style={{ opacity: 0.6 }}>Archivé</span>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 24 }}>Aucun employé.</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ padding: '13px 20px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)', fontSize: 12.5, color: 'var(--ink-3)' }}>
            <strong style={{ color: 'var(--ink)', fontFamily: 'var(--mono)' }}>{rows.length}</strong> employés affichés
          </div>
        </div>
      )}
    </>
  );
}

// ─── Detail page — the Fiche Employé island (proto port) ─────────

interface DetailForm {
  name: string;
  entityId: string;
  code: string;
  email: string;
  phone: string;
  department: string;
  hiredAt: string;
  dateSortie: string;
  probationStart: string;
  probationMonths: string;
  contractType: string; // 'CDI' | 'CDD'
  ttDays: number[]; // hybride — jours TT (0=dim … 6=sam)
  payActualHours: boolean; // profil horaire — journées courtes comptées telles quelles
  cddEnd: string;
  cddMotif: string;
  civility: string;
  birthDate: string;
  birthPlace: string;
  cnasNumber: string;
  salary: string; // reference salary (DA) — managed here, per-month overrides live on Salaires & bonus
  bankLabel: string;
  bankAccount: string;
  bankSwift: string;
  paymentMethod: PaymentMethod;
  emergency1Name: string;
  emergency1Phone: string;
  emergency1Relation: string;
  emergency2Name: string;
  emergency2Phone: string;
  emergency2Relation: string;
  congeBalance: string; // create-only opening stock
  recupBalance: string;
}

function formFromEmp(emp: EmployeeFullDTO): DetailForm {
  return {
    name: emp.name,
    entityId: emp.entityId,
    code: emp.code,
    email: emp.email ?? '',
    phone: emp.phone ?? '',
    department: emp.department ?? '',
    hiredAt: isoDay(emp.hiredAt),
    dateSortie: isoDay(emp.dateSortie),
    probationStart: isoDay(emp.probationStart),
    probationMonths: emp.probationMonths != null ? String(emp.probationMonths) : '',
    contractType: emp.contractType ?? 'CDI',
    // Legacy ven/sam entries are dropped silently — they never applied.
    ttDays: (emp.ttDays ?? []).filter((d) => d <= 4),
    payActualHours: emp.payActualHours ?? false,
    cddEnd: isoDay(emp.cddEnd),
    cddMotif: emp.cddMotif ?? '',
    civility: emp.civility ?? '',
    birthDate: isoDay(emp.birthDate),
    birthPlace: emp.birthPlace ?? '',
    cnasNumber: emp.cnasNumber ?? '',
    salary: emp.salary != null && emp.salary > 0 ? String(emp.salary) : '',
    bankLabel: emp.bankLabel ?? '',
    bankAccount: emp.bankAccount ?? '',
    bankSwift: emp.bankSwift ?? '',
    paymentMethod: emp.paymentMethod ?? 'VIREMENT',
    emergency1Name: emp.emergency1Name ?? '',
    emergency1Phone: emp.emergency1Phone ?? '',
    emergency1Relation: emp.emergency1Relation ?? '',
    emergency2Name: emp.emergency2Name ?? '',
    emergency2Phone: emp.emergency2Phone ?? '',
    emergency2Relation: emp.emergency2Relation ?? '',
    congeBalance: '',
    recupBalance: '',
  };
}

// Semaine algérienne uniquement (dim→jeu) — ven/sam sont le week-end,
// un jour TT hors jours ouvrés ne s'appliquerait jamais.
const TT_DAY_CHOICES: ReadonlyArray<{ dow: number; label: string }> = [
  { dow: 0, label: 'Dim' }, { dow: 1, label: 'Lun' }, { dow: 2, label: 'Mar' },
  { dow: 3, label: 'Mer' }, { dow: 4, label: 'Jeu' },
];
const TT_ALL_WORKDAYS = [0, 1, 2, 3, 4];

interface SectionDef { id: string; icon: LucideIcon; t: string; d: string; }
const SECTIONS: SectionDef[] = [
  { id: 'identite', icon: IdCard, t: 'Identité · affectation', d: 'Nom-clé d’appariement pointeuse, entité et coordonnées' },
  { id: 'cycle', icon: LifeBuoy, t: 'Cycle de vie · période d’essai', d: 'Entrée, sortie et suivi de la période d’essai' },
  { id: 'postes', icon: Briefcase, t: 'Postes', d: 'Affectation actuelle et historique des postes' },
  { id: 'salaire', icon: Banknote, t: 'Salaire · banque', d: 'Salaire de référence, historique et coordonnées bancaires' },
  { id: 'rh', icon: FileText, t: 'Informations RH · attestation', d: 'État civil et données pour attestations' },
  { id: 'urgence', icon: PhoneCall, t: 'Contacts d’urgence', d: 'Qui prévenir en cas de problème — deux contacts' },
  { id: 'statut', icon: AlertTriangle, t: 'Statut', d: 'Archivage et fin de contrat' },
];

function EmployeeDetailPage({
  employeeId,
  startEdit,
  entities,
  defaultEntityId,
  onBack,
  onCreated,
}: {
  employeeId: string | null;
  startEdit: boolean;
  entities: EntityDTO[];
  defaultEntityId: string;
  onBack: () => void;
  onCreated: (id: string) => void;
}) {
  const isCreate = employeeId === null;
  const employees = useEmployeesFull();
  const emp = isCreate ? null : (employees.data ?? []).find((e) => e.id === employeeId) ?? null;
  const entity = emp ? entities.find((e) => e.id === emp.entityId) : entities.find((e) => e.id === defaultEntityId);

  const themeDark = useUiStore((s) => s.theme) === 'dark';

  const update = useUpdateEmployee();
  const create = useCreateEmployee();
  const deactivate = useDeactivateEmployee();

  const blank: DetailForm = {
    name: '', entityId: defaultEntityId, code: '', email: '', phone: '', department: '',
    hiredAt: '', dateSortie: '', probationStart: '', probationMonths: '',
    contractType: 'CDI', ttDays: [], payActualHours: false, cddEnd: '', cddMotif: '',
    civility: '', birthDate: '', birthPlace: '', cnasNumber: '',
    salary: '', bankLabel: '', bankAccount: '', bankSwift: '', paymentMethod: 'VIREMENT', congeBalance: '', recupBalance: '',
    emergency1Name: '', emergency1Phone: '', emergency1Relation: '',
    emergency2Name: '', emergency2Phone: '', emergency2Relation: '',
  };
  const baseForm = emp ? formFromEmp(emp) : blank;

  const [editing, setEditing] = useState(isCreate || startEdit);
  const [form, setForm] = useState<DetailForm>(baseForm);
  const [orig, setOrig] = useState<DetailForm>(baseForm);
  const [modal, setModal] = useState(false);
  const [focusKey, setFocusKey] = useState<keyof DetailForm | null>(null);
  const [active, setActive] = useState('identite');

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const secRefs = useRef<Record<string, HTMLElement | null>>({});

  // Opened straight from a URL (?emp=…), the roster hasn't loaded yet on
  // first render, so the form initialises blank. Hydrate it once the
  // record arrives — guarded by a ref so it never clobbers live edits.
  const hydrated = useRef(isCreate || emp !== null);
  useEffect(() => {
    if (hydrated.current || !emp) return;
    hydrated.current = true;
    const f = formFromEmp(emp);
    setForm(f);
    setOrig(f);
  }, [emp]);

  const set = (k: keyof DetailForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const dirty = JSON.stringify(form) !== JSON.stringify(orig);
  const dirtyCount = (Object.keys(form) as (keyof DetailForm)[]).filter((k) => form[k] !== orig[k]).length;
  const nameChanged = !isCreate && !!emp && form.name.trim() !== emp.name.trim();
  const canSave = isCreate ? (dirty && !!form.name.trim() && !!form.entityId) : dirty;
  const saving = create.isPending || update.isPending;

  const me = useMe();
  const perms = me.data?.permissions ?? [];
  const canPayroll = perms.includes('payroll.view');
  // Two keys, one smaller than the other. `employees.edit` opens the
  // whole fiche; `employees.edit_limited` opens exactly what an
  // assistant needs for the paperwork — creating someone, and keeping
  // their date de sortie and emergency contacts current. The server
  // enforces the same list; this only stops the UI from offering a
  // field that would come back 403.
  const canEditFull = perms.includes('employees.edit');
  const canEditAny = canEditFull || perms.includes('employees.edit_limited');
  const LIMITED_CREATE: ReadonlyArray<keyof DetailForm> = ['name', 'entityId', 'hiredAt'];
  const LIMITED_PATCH: ReadonlyArray<keyof DetailForm> = [
    'dateSortie',
    'emergency1Name', 'emergency1Phone', 'emergency1Relation',
    'emergency2Name', 'emergency2Phone', 'emergency2Relation',
  ];
  const fieldEditable = (k: keyof DetailForm): boolean => {
    if (canEditFull) return true;
    if (!canEditAny) return false;
    return (isCreate ? LIMITED_CREATE : LIMITED_PATCH).includes(k);
  };

  const visibleSections = isCreate
    ? SECTIONS.filter((s) => s.id === 'identite' || s.id === 'cycle' || (s.id === 'rh' && canEditFull))
    : SECTIONS.filter(
        (s) => (s.id !== 'salaire' || canPayroll) && (s.id !== 'statut' || canEditFull),
      );
  // Statut is a danger section hidden in view mode (CSS) — drop it from
  // the jump-nav + scroll-spy until edit mode reveals it.
  const navSections = editing ? visibleSections : visibleSections.filter((s) => s.id !== 'statut');

  // ---- mode transitions ----
  const enterEdit = (k?: keyof DetailForm) => {
    if (!editing) {
      const f = emp ? formFromEmp(emp) : blank;
      setForm(f); setOrig(f);
    }
    setEditing(true);
    if (k) setFocusKey(k);
  };

  const cancel = () => {
    if (dirty && !window.confirm('Abandonner les modifications non enregistrées ?')) return;
    if (isCreate) { onBack(); return; }
    setForm(orig);
    setEditing(false);
  };

  const numOpt = (s: string) => {
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };

  // Renvoie vrai quand la fiche est enregistrée — le garde-fou de sortie
  // (changement de société / page) s'en sert pour ne pas partir sur un
  // échec.
  const save = async (): Promise<boolean> => {
    if (!form.name.trim() || !form.entityId) {
      toast.danger('Nom et entité sont obligatoires.');
      return false;
    }
    if (isCreate) {
      try {
        // Le droit limité crée la personne, pas son dossier : nom, entité,
        // date d'entrée. Le code EMPxxx reste auto-généré, le reste vide —
        // et le serveur refuse tout champ en trop.
        const created = await create.mutateAsync(
          canEditFull
            ? {
                name: form.name.trim(),
                entityId: form.entityId,
                code: form.code.trim() || undefined,
                email: form.email.trim() || null,
                department: form.department.trim() || null,
                phone: form.phone.trim() || null,
                hiredAt: form.hiredAt || null,
                probationStart: form.probationStart || null,
                probationMonths: form.probationMonths ? Number(form.probationMonths) : null,
                contractType: form.contractType === 'CDD' ? 'CDD' : 'CDI',
                ttDays: form.ttDays,
                payActualHours: form.payActualHours,
                cddEnd: (form.contractType === 'CDD' && form.cddEnd) || null,
                cddMotif: (form.contractType === 'CDD' && form.cddMotif.trim()) || null,
                congeBalance: numOpt(form.congeBalance),
                recupBalance: numOpt(form.recupBalance),
              }
            : {
                name: form.name.trim(),
                entityId: form.entityId,
                ...(form.hiredAt ? { hiredAt: form.hiredAt } : {}),
              },
        );
        toast.success('Employé ajouté.');
        onCreated(created.id);
        return true;
      } catch (err) {
        const body = (err as { body?: { error?: string } }).body;
        toast.danger(body?.error === 'duplicate_code' ? 'Code déjà utilisé.' : 'Échec de la création.');
        return false;
      }
    }
    // Only the fields this viewer may write. The server rejects the rest
    // with a 403 listing them, so sending them anyway would turn a
    // legitimate edit into an error.
    const emergency: EmployeePatch = {
      emergency1Name: form.emergency1Name.trim() || null,
      emergency1Phone: form.emergency1Phone.trim() || null,
      emergency1Relation: form.emergency1Relation.trim() || null,
      emergency2Name: form.emergency2Name.trim() || null,
      emergency2Phone: form.emergency2Phone.trim() || null,
      emergency2Relation: form.emergency2Relation.trim() || null,
    };
    const patch: EmployeePatch = canEditFull
      ? {
          name: form.name.trim(),
          entityId: form.entityId,
          email: form.email.trim() || null,
          phone: form.phone || null,
          department: form.department || null,
          hiredAt: form.hiredAt || null,
          dateSortie: form.dateSortie || null,
          probationStart: form.probationStart || null,
          probationMonths: form.probationMonths ? Number(form.probationMonths) : null,
          contractType: form.contractType === 'CDD' ? 'CDD' : 'CDI',
          ttDays: form.ttDays,
          payActualHours: form.payActualHours,
          cddEnd: form.cddEnd || null,
          cddMotif: form.cddMotif.trim() || null,
          civility: form.civility || null,
          birthDate: form.birthDate || null,
          birthPlace: form.birthPlace || null,
          cnasNumber: form.cnasNumber || null,
          ...(form.salary.trim() !== '' ? { salary: Number(form.salary.replace(/\s/g, '')) } : {}),
          bankLabel: form.bankLabel.trim() || null,
          bankAccount: form.bankAccount.trim() || null,
          bankSwift: form.bankSwift.trim() || null,
          paymentMethod: form.paymentMethod,
          ...emergency,
        }
      : { dateSortie: form.dateSortie || null, ...emergency };
    try {
      const updated = await update.mutateAsync({ id: emp!.id, patch });
      toast.success('Fiche enregistrée.');
      const f = formFromEmp(updated);
      setForm(f); setOrig(f);
      setEditing(false);
      return true;
    } catch {
      toast.danger("Échec de l'enregistrement.");
      return false;
    }
  };

  // Brouillon déclaré au registre : changer de société, de page ou
  // fermer l'onglet passe par la popup « Modifications non enregistrées ».
  useUnsavedGuard({
    id: 'employee-form',
    dirty: editing && dirty,
    label: isCreate ? 'Nouvel employé' : `Fiche de ${emp?.name ?? (form.name.trim() || 'l’employé')}`,
    summary: `${dirtyCount} modification${dirtyCount > 1 ? 's' : ''}`,
    save: canSave ? async () => { if (!(await save())) throw new Error('save_failed'); } : undefined,
    discard: () => {
      setForm(orig);
      if (isCreate) onBack();
      else setEditing(false);
    },
  });

  const toggleActive = async () => {
    if (!emp) return;
    try {
      if (emp.active) {
        await deactivate.mutateAsync(emp.id);
        toast.success('Employé archivé.');
      } else {
        await update.mutateAsync({ id: emp.id, patch: { active: true } });
        toast.success('Employé réactivé.');
      }
    } catch {
      toast.danger('Échec du changement de statut.');
    }
  };

  const toggleStatsExcluded = async () => {
    if (!emp) return;
    try {
      const next = !emp.statsExcluded;
      await update.mutateAsync({ id: emp.id, patch: { statsExcluded: next } });
      toast.success(next ? 'Exclu des statistiques d’équipe.' : 'Réintégré dans les statistiques.');
    } catch {
      toast.danger('Échec du changement.');
    }
  };

  const togglePointageExcluded = async () => {
    if (!emp) return;
    try {
      const next = !emp.pointageExcluded;
      await update.mutateAsync({ id: emp.id, patch: { pointageExcluded: next } });
      toast.success(next
        ? 'Retiré du pointage : saisie, grille, imports et relances.'
        : 'Réintégré au pointage.');
    } catch {
      toast.danger('Échec du changement.');
    }
  };

  const togglePayrollExcluded = async () => {
    if (!emp) return;
    try {
      const next = !emp.payrollExcluded;
      await update.mutateAsync({ id: emp.id, patch: { payrollExcluded: next } });
      toast.success(next
        ? 'Retiré de la paie : plus de ligne de salaire ni de virement.'
        : 'Réintégré à la paie.');
    } catch {
      toast.danger('Échec du changement.');
    }
  };

  // focus the clicked field after entering edit
  useEffect(() => {
    if (editing && focusKey) {
      const el = document.querySelector<HTMLElement>(`[data-field="${focusKey}"]`);
      if (el) { el.focus(); const inp = el as HTMLInputElement; if (inp.select) { try { inp.select(); } catch { /* noop */ } } }
      setFocusKey(null);
    }
  }, [editing, focusKey]);

  // Esc cancels edit (with dirty-confirm). Existing records only —
  // create has its own back affordance.
  useEffect(() => {
    if (!editing || isCreate) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, dirty, form, orig, isCreate]);

  // scroll-spy on the bounded .emp-scroll container (rect-relative;
  // deferred + re-run on mode change), exactly like the proto.
  useEffect(() => {
    const root = scrollRef.current; if (!root) return;
    const compute = () => {
      const rTop = root.getBoundingClientRect().top;
      let cur = navSections[0].id;
      for (const s of navSections) {
        const el = secRefs.current[s.id];
        // offsetParent === null ⇒ display:none (hidden danger section) —
        // its rect is all-zeros and would falsely register as "at top".
        if (el && el.offsetParent !== null && el.getBoundingClientRect().top - rTop <= 130) cur = s.id;
      }
      setActive(cur);
    };
    root.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    const raf = requestAnimationFrame(() => requestAnimationFrame(compute));
    return () => {
      root.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, isCreate]);

  const jump = (id: string) => {
    const el = secRefs.current[id], root = scrollRef.current;
    if (!el || !root) return;
    const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 16;
    root.scrollTo({ top, behavior: 'smooth' });
  };

  // Existing record not in cache yet (rare — the list usually warms it).
  if (!isCreate && !emp) {
    return (
      <div className={'emp' + (themeDark ? ' theme-dark' : '')}>
        <div className="emp-scroll"><div className="emp-wrap">
          <button className="backbtn" onClick={onBack} data-testid="emp-back"><ArrowLeft /> Retour aux employés</button>
          <p className="page-placeholder">{employees.isLoading ? 'Chargement…' : 'Employé introuvable.'}</p>
        </div></div>
      </div>
    );
  }

  const headerName = isCreate ? 'Nouvel employé' : emp!.name;
  const av = initials(headerName);

  // live (recomputes as the user edits) — proto §6.
  const srcStart = editing ? form.probationStart : isoDay(emp?.probationStart);
  const srcMonths = editing ? form.probationMonths : (emp?.probationMonths != null ? String(emp!.probationMonths) : '');
  const srcHired = editing ? form.hiredAt : isoDay(emp?.hiredAt);
  const srcSortie = editing ? form.dateSortie : isoDay(emp?.dateSortie);
  const essaiM = parseInt(srcMonths, 10) || 0;
  const essaiFin = addMonths(srcStart, essaiM);
  const essaiDays = essaiFin ? daysFromToday(essaiFin) : null;
  const essaiPassed = essaiDays != null && essaiDays < 0;
  const tenure = monthsBetween(srcHired, srcSortie);
  const essaiPct = tenure ? Math.min(100, (essaiM / Math.max(tenure, essaiM || 1)) * 100) : 0;

  const fp = { editing, onEdit: enterEdit, canEditField: fieldEditable };

  return (
    <div
      className={'emp' + (editing ? ' editing' : '') + (themeDark ? ' theme-dark' : '')}
      data-testid="emp-detail"
    >
      <div className="emp-scroll" ref={scrollRef}>
        <div className="emp-wrap">
          <button className="backbtn" onClick={onBack} data-testid="emp-back">
            <ArrowLeft /> Retour aux employés
          </button>

          {/* identity header */}
          <div className="hd">
            <div className="hd-av">{av}</div>
            <div className="hd-id">
              <h1 className="hd-name">{headerName}</h1>
              <div className="hd-meta">
                {!isCreate && <span className="tag mat">{emp!.department || 'Sans fonction'}</span>}
                {entity && <span className="tag ent"><span className="d" />{entity.short}{!isCreate ? ` · ${emp!.code}` : ''}</span>}
                {!isCreate && (emp!.active
                  ? <span className="tag actif"><span className="d" />Actif</span>
                  : <span className="tag archived"><span className="d" />Archivé</span>)}
              </div>
            </div>
            <div className="hd-act">
              {editing ? (
                <span className="editpill"><span className="d" />Mode édition</span>
              ) : (
                <>
                  <a className="btn ghost" href={`/api/employees/${emp!.id}/attestation`} target="_blank" rel="noreferrer" data-testid="emp-attestation">
                    <Printer /> Attestation
                  </a>
                  {canEditAny && (
                    <button className="btn primary" onClick={() => enterEdit()} data-testid="emp-edit">
                      <Pencil /> Modifier la fiche
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="stage">
            {/* ---------- LEFT RAIL ---------- */}
            <div className="rail">
              {isCreate ? (
                <div className="rcard">
                  <div className="rcard-h">Soldes initiaux</div>
                  <div className="quick">
                    <div className="f">
                      <label className="f-l">Solde congé initial</label>
                      <input className="ctl" value={form.congeBalance} onChange={set('congeBalance')} data-testid="emp-init-conge" aria-label="Solde congé initial" placeholder="0" inputMode="decimal" />
                    </div>
                    <div className="f">
                      <label className="f-l">Solde récup initial</label>
                      <input className="ctl" value={form.recupBalance} onChange={set('recupBalance')} data-testid="emp-init-recup" aria-label="Solde récup initial" placeholder="0" inputMode="decimal" />
                    </div>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--ink4)' }}>
                      Enregistrés comme stock d’ouverture dans le journal des congés.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rcard">
                  <div className="rcard-h">Soldes</div>
                  <div className="balrow">
                    <div className="bal"><div className={'bal-v' + (emp!.congeBalance === 0 ? ' zero' : '')}>{fr1(emp!.congeBalance)}</div><div className="bal-l">jours congé</div></div>
                    <div className="bal"><div className={'bal-v' + (emp!.recupBalance === 0 ? ' zero' : '')}>{fr1(emp!.recupBalance)}</div><div className="bal-l">jours récup</div></div>
                  </div>
                  <button className="bal-adjust" onClick={() => setModal(true)}>＋ Ajuster un solde</button>
                </div>
              )}

              <div className="rcard">
                <div className="rcard-h">En bref</div>
                <div className="quick">
                  <div className="quick-row"><span className="quick-l">Ancienneté</span><span className="quick-v">{fmtTenure(tenure)}</span></div>
                  <div className="quick-row"><span className="quick-l">Entrée</span><span className="quick-v mono">{frDate(srcHired) || '—'}</span></div>
                  <div className="quick-row"><span className="quick-l">Matricule</span><span className="quick-v mono">{isCreate ? 'auto' : emp!.code}</span></div>
                  <div className="quick-row"><span className="quick-l">Entité</span><span className="quick-v">{entity?.name ?? '—'}</span></div>
                </div>
              </div>

              <div className="rcard">
                <div className="jump">
                  {navSections.map((s) => (
                    <button key={s.id} className={'jump-i' + (active === s.id ? ' on' : '')} onClick={() => jump(s.id)}>
                      <span className="n" />{s.t.split(' · ')[0]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ---------- FORM ---------- */}
            <div className="form fade" key={editing ? 'edit' : 'view'}>

              {/* IDENTITÉ */}
              <Section s={SECTIONS[0]} refCb={(el) => { secRefs.current.identite = el; }}>
                <div className="grid">
                  <Field {...fp} k="name" full label="Nom" req display={form.name}
                         hint={<span className="f-hint key"><Key /> Clé d’appariement avec la pointeuse — modifier avec prudence</span>}>
                    <input className="ctl" data-field="name" data-testid="emp-name" aria-label="Nom" value={form.name} onChange={set('name')} placeholder={isCreate ? 'ex. ZOUBIRI Ilyes' : undefined} />
                    {nameChanged && (
                      <p data-testid="emp-name-warning" style={{ margin: 0, fontSize: 11, color: 'var(--warnc)', lineHeight: 1.45 }}>
                        ⚠︎ Renommer interrompt la correspondance des pointages : la pointeuse exporte
                        encore l’ancien nom, donc les pointages futurs de cet employé seront ignorés à
                        l’import jusqu’à ce que le nom soit mis à jour sur la pointeuse (ou rétabli ici).
                      </p>
                    )}
                  </Field>
                  <Field {...fp} k="entityId" label="Entité" req display={entity?.name ?? form.entityId}>
                    <select className="ctl" data-field="entityId" data-testid="emp-entity" aria-label="Entité" value={form.entityId} onChange={set('entityId')}>
                      {isCreate && <option value="" disabled>— choisir —</option>}
                      {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </Field>
                  {isCreate && (
                    <Field {...fp} k="code" label="Code" display={form.code}>
                      <input className="ctl" data-field="code" data-testid="emp-code" aria-label="Code" value={form.code} onChange={set('code')} placeholder="auto (EMP###)" />
                    </Field>
                  )}
                  <Field {...fp} k="department" label="Fonction" display={form.department}>
                    <input className="ctl" data-field="department" data-testid="emp-department" aria-label="Fonction" value={form.department} onChange={set('department')} placeholder="Ex. Opérateur de production" />
                  </Field>
                  <Field {...fp} k="email" full label="Email" display={form.email}>
                    <input className="ctl" type="email" data-field="email" data-testid="emp-email" aria-label="Email" value={form.email} onChange={set('email')} placeholder="prenom.nom@shiftin.dz" />
                  </Field>
                  <Field {...fp} k="phone" label="Téléphone" display={form.phone}>
                    <input className="ctl" data-field="phone" data-testid="emp-phone" aria-label="Téléphone" value={form.phone} onChange={set('phone')} placeholder="Ex. 0550 12 34 56" />
                  </Field>
                </div>
              </Section>

              {/* CYCLE DE VIE */}
              <Section s={SECTIONS[1]} refCb={(el) => { secRefs.current.cycle = el; }}>
                <div className="grid">
                  <Field {...fp} k="hiredAt" label="Date d’entrée" req hint="Format jj/mm/aaaa"
                         display={form.hiredAt ? <Mono>{frDate(form.hiredAt)}</Mono> : ''}>
                    <input className="ctl" type="date" data-field="hiredAt" data-testid="emp-hired" aria-label="Date d’entrée" value={form.hiredAt} onChange={set('hiredAt')} />
                  </Field>
                  {!isCreate && (
                    <Field {...fp} k="dateSortie" label="Date de sortie" hint="Laisser vide si toujours en poste"
                           display={form.dateSortie ? <Mono>{frDate(form.dateSortie)}</Mono> : <span className="f-val-ok">Toujours en poste</span>}>
                      <input className="ctl" type="date" data-field="dateSortie" data-testid="emp-sortie" aria-label="Date de sortie" value={form.dateSortie} onChange={set('dateSortie')} />
                    </Field>
                  )}
                  <Field {...fp} k="contractType" label="Type de contrat"
                         display={form.contractType === 'CDD' ? 'CDD' : 'CDI'}>
                    <select className="ctl" data-field="contractType" data-testid="emp-contract-type" aria-label="Type de contrat" value={form.contractType} onChange={set('contractType')}>
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                    </select>
                  </Field>
                  {form.contractType === 'CDD' && (
                    <>
                      <Field {...fp} k="cddEnd" label="Fin de CDD" hint="Terme du contrat en cours"
                             display={form.cddEnd ? <Mono>{frDate(form.cddEnd)}</Mono> : ''}>
                        <input className="ctl" type="date" data-field="cddEnd" data-testid="emp-cdd-end" aria-label="Fin de CDD" value={form.cddEnd} onChange={set('cddEnd')} />
                      </Field>
                      <Field {...fp} k="cddMotif" label="Motif du CDD"
                             display={form.cddMotif}>
                        <input className="ctl" data-field="cddMotif" data-testid="emp-cdd-motif" aria-label="Motif du CDD" value={form.cddMotif} onChange={set('cddMotif')} placeholder="Ex. Surcroît d’activité, remplacement…" />
                      </Field>
                    </>
                  )}
                  <Field {...fp} k="probationStart" label="Début période d’essai"
                         display={form.probationStart ? <Mono>{frDate(form.probationStart)}</Mono> : ''}>
                    <input className="ctl" type="date" data-field="probationStart" data-testid="emp-prob-start" aria-label="Début période d’essai" value={form.probationStart} onChange={set('probationStart')} />
                  </Field>
                  <Field {...fp} k="probationMonths" label="Durée d’essai"
                         display={form.probationMonths ? `${form.probationMonths} mois` : ''}>
                    <div className="suffix-wrap">
                      <input className="ctl with-suffix" type="number" min="0" data-field="probationMonths" data-testid="emp-prob-months" aria-label="Durée période d’essai (mois)" value={form.probationMonths} onChange={set('probationMonths')} placeholder="3" />
                      <span className="suffix">mois</span>
                    </div>
                  </Field>

                  {essaiFin && (
                    <div className={'essai-card ' + (essaiPassed ? 't-ok' : 't-warn')} data-testid="emp-prob-end">
                      <span className="ic"><Flag /></span>
                      <div className="meta">
                        <span className="lbl">Fin de période d’essai</span>
                        <span className="date mono">{frDate(essaiFin)}</span>
                      </div>
                      <span className="pill">{essaiPassed ? 'Validée · ' : 'En cours · fin '}{relFr(essaiDays!)}</span>
                    </div>
                  )}

                  <div className="tenure">
                    <div className="tenure-top">
                      <span className="tenure-lbl">Ancienneté dans l’entité</span>
                      <span className="tenure-val"><em>{fmtTenure(tenure)}</em>{essaiM ? ` · essai ${essaiM} mois` : ''}</span>
                    </div>
                    <div className="tbar">
                      <i className="essai" style={{ width: essaiPct + '%' }} />
                      <i className="conf" style={{ width: (100 - essaiPct) + '%' }} />
                    </div>
                    <div className="tlegend">
                      <span className="tleg"><span className="sw" style={{ background: 'repeating-linear-gradient(45deg,var(--warn),var(--warn) 3px,#fff 3px,#fff 6px)' }} />Période d’essai</span>
                      <span className="tleg"><span className="sw" style={{ background: 'var(--green)' }} />Confirmé</span>
                    </div>
                  </div>

                  {/* TÉLÉTRAVAIL HYBRIDE — jours TT automatiques */}
                  <div className="ttdays" data-testid="emp-ttdays">
                    <div className="ttdays-top">
                      <span className="ttdays-lbl">Télétravail (hybride)</span>
                      <span className="ttdays-val">
                        {form.ttDays.length === 0
                          ? 'Aucun jour TT — 100 % sur site'
                          : form.ttDays.length >= TT_ALL_WORKDAYS.length
                            ? 'TT complet — 100 % télétravail'
                            : form.ttDays.length + (form.ttDays.length > 1 ? ' jours TT / semaine' : ' jour TT / semaine')}
                      </span>
                    </div>
                    <div className="ttdays-chips">
                      {TT_DAY_CHOICES.map(({ dow, label }) => {
                        const sel = form.ttDays.includes(dow);
                        return (
                          <button key={dow} type="button"
                            className={'ttday' + (sel ? ' sel' : '')}
                            disabled={!editing}
                            data-testid={'emp-ttday-' + dow}
                            aria-pressed={sel}
                            aria-label={'Jour TT — ' + label}
                            onClick={() => setForm((f) => ({
                              ...f,
                              ttDays: sel ? f.ttDays.filter((x) => x !== dow) : [...f.ttDays, dow].sort(),
                            }))}>
                            {label}
                          </button>
                        );
                      })}
                      {(() => {
                        const full = TT_ALL_WORKDAYS.every((d) => form.ttDays.includes(d));
                        return (
                          <button type="button"
                            className={'ttday ttday-full' + (full ? ' sel' : '')}
                            disabled={!editing}
                            data-testid="emp-ttday-full"
                            aria-pressed={full}
                            aria-label="TT complet — toute la semaine en télétravail"
                            onClick={() => setForm((f) => ({
                              ...f,
                              ttDays: full ? [] : [...TT_ALL_WORKDAYS],
                            }))}>
                            TT complet
                          </button>
                        );
                      })()}
                    </div>
                    <div className="ttdays-hint">
                      Ces jours se marquent <b>TT automatiquement</b> quand aucun pointage n’est saisi —
                      sans arbitrage, sans « prévenu ? ». Un congé approuvé, un pointage réel ou une
                      décision manuelle (ex. absence injustifiée) gardent toujours la priorité.
                    </div>
                  </div>

                  {/* HEURES RÉELLES EN PAIE — profils horaires / temps partiel */}
                  <div className="ttdays" data-testid="emp-payactual">
                    <div className="ttdays-top">
                      <span className="ttdays-lbl">Heures réelles en paie</span>
                      <span className="ttdays-val">
                        {form.payActualHours ? 'Journées comptées telles quelles' : 'Journée standard (défaut)'}
                      </span>
                    </div>
                    <div className="ttdays-chips">
                      <button type="button"
                        className={'ttday' + (form.payActualHours ? ' sel' : '')}
                        disabled={!editing}
                        data-testid="emp-payactual-toggle"
                        aria-pressed={form.payActualHours}
                        aria-label="Compter les heures réelles en paie"
                        onClick={() => setForm((f) => ({ ...f, payActualHours: !f.payActualHours }))}>
                        Heures réelles
                      </button>
                    </div>
                    <div className="ttdays-hint">
                      Pour un <b>temps partiel ou un profil horaire</b> : ses journées courtes ne passent plus
                      par l’arbitrage de la grille — la colonne paie compte les heures pointées, telles quelles.
                      Le salaire versé ne change pas : « Total h paie » est un document de contrôle, pas un calcul
                      de rémunération.
                    </div>
                  </div>
                </div>
              </Section>

              {/* POSTES (existing records only) */}
              {!isCreate && emp && (
                <Section s={SECTIONS[2]} refCb={(el) => { secRefs.current.postes = el; }}>
                  <PosteSection emp={emp} />
                </Section>
              )}

              {/* SALAIRE · BANQUE (existing records + payroll.view only) */}
              {!isCreate && emp && canPayroll && (
                <Section s={SECTIONS[3]} refCb={(el) => { secRefs.current.salaire = el; }}>
                  <div className="grid" style={{ marginBottom: 14 }}>
                    <Field {...fp} k="salary" label="Salaire de référence"
                           hint="Salaire net mensuel de base (DA). L'écran Salaires & bonus le reprend et peut le remplacer pour un mois donné."
                           display={form.salary ? <Mono>{fmtDA(Number(form.salary))} DA</Mono> : 'Aucun salaire enregistré'}>
                      <input className="ctl mono" data-field="salary" data-testid="emp-salary" aria-label="Salaire de référence"
                             value={form.salary} onChange={set('salary')} inputMode="numeric" placeholder="Ex. 65000" />
                    </Field>
                  </div>
                  <SalaryHistory emp={emp} />
                  <div className="grid" style={{ marginTop: 14 }}>
                    <Field {...fp} k="bankLabel" label="Libellé bancaire" hint="« Nom de la contrepartie » du fichier banque — tel qu'écrit sur le compte."
                           display={form.bankLabel}>
                      <input className="ctl" data-field="bankLabel" data-testid="emp-banklabel" aria-label="Libellé bancaire" value={form.bankLabel} onChange={set('bankLabel')} placeholder="Ex. KHARFANE Badis" />
                    </Field>
                    <Field {...fp} k="bankAccount" label="N° de compte" display={form.bankAccount ? <Mono>{form.bankAccount}</Mono> : ''}>
                      <input className="ctl mono" data-field="bankAccount" data-testid="emp-bankaccount" aria-label="Numéro de compte" value={form.bankAccount} onChange={set('bankAccount')} inputMode="numeric" placeholder="20 chiffres" />
                    </Field>
                    <Field {...fp} k="bankSwift" label="Code SWIFT" display={form.bankSwift ? <Mono>{form.bankSwift}</Mono> : ''}>
                      <input className="ctl mono" data-field="bankSwift" data-testid="emp-bankswift" aria-label="Code SWIFT" value={form.bankSwift} onChange={(e) => setForm((f) => ({ ...f, bankSwift: e.target.value.toUpperCase() }))} placeholder="Ex. EPALDZALXXX" />
                    </Field>
                    <Field {...fp} k="paymentMethod" label="Mode de paiement"
                           hint="Seuls les virements partent dans le fichier banque. Chèque / payé à part : le salaire reste dans la paie, listé hors fichier."
                           display={PAYMENT_METHODS[form.paymentMethod].label}>
                      <select className="ctl" data-field="paymentMethod" data-testid="emp-payment-method" aria-label="Mode de paiement" value={form.paymentMethod}
                              onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value as PaymentMethod }))}>
                        {PAYMENT_METHOD_KEYS.map((k) => <option key={k} value={k}>{PAYMENT_METHODS[k].label}</option>)}
                      </select>
                    </Field>
                  </div>
                </Section>
              )}

              {/* RH */}
              <Section s={SECTIONS[4]} refCb={(el) => { secRefs.current.rh = el; }}>
                <div className="grid">
                  <Field {...fp} k="civility" label="Civilité" display={form.civility}>
                    <select className="ctl" data-field="civility" data-testid="emp-civility" aria-label="Civilité" value={form.civility} onChange={set('civility')}>
                      {CIVILITIES.map((c) => <option key={c} value={c}>{c || '—'}</option>)}
                    </select>
                  </Field>
                  <Field {...fp} k="birthDate" label="Date de naissance"
                         display={form.birthDate ? <Mono>{frDate(form.birthDate)}</Mono> : ''}>
                    <input className="ctl" type="date" data-field="birthDate" data-testid="emp-birthdate" aria-label="Date de naissance" value={form.birthDate} onChange={set('birthDate')} />
                  </Field>
                  <Field {...fp} k="birthPlace" label="Lieu de naissance" display={form.birthPlace}>
                    <input className="ctl" data-field="birthPlace" data-testid="emp-birthplace" aria-label="Lieu de naissance" value={form.birthPlace} onChange={set('birthPlace')} placeholder="Ex. Alger" />
                  </Field>
                  <Field {...fp} k="cnasNumber" label="N° CNAS" display={form.cnasNumber ? <Mono>{form.cnasNumber}</Mono> : ''}>
                    <input className="ctl" data-field="cnasNumber" data-testid="emp-cnas" aria-label="Numéro CNAS" value={form.cnasNumber} onChange={set('cnasNumber')} placeholder="Ex. 12 3456789 01" />
                  </Field>
                </div>
              </Section>

              {/* CONTACTS D'URGENCE */}
              {!isCreate && (
                <Section s={SECTIONS[5]!} refCb={(el) => { secRefs.current.urgence = el; }}>
                  <div className="grid">
                    <Field {...fp} k="emergency1Name" label="Contact 1 · nom" display={form.emergency1Name}>
                      <input className="ctl" data-field="emergency1Name" data-testid="emp-em1-name" aria-label="Contact d’urgence 1 — nom" value={form.emergency1Name} onChange={set('emergency1Name')} placeholder="Ex. BENALI Karim" />
                    </Field>
                    <Field {...fp} k="emergency1Phone" label="Contact 1 · téléphone" display={form.emergency1Phone ? <Mono>{form.emergency1Phone}</Mono> : ''}>
                      <input className="ctl" data-field="emergency1Phone" data-testid="emp-em1-phone" aria-label="Contact d’urgence 1 — téléphone" value={form.emergency1Phone} onChange={set('emergency1Phone')} placeholder="Ex. 0550 12 34 56" />
                    </Field>
                    <Field {...fp} k="emergency1Relation" label="Contact 1 · lien" display={form.emergency1Relation}>
                      <input className="ctl" data-field="emergency1Relation" data-testid="emp-em1-relation" aria-label="Contact d’urgence 1 — lien avec l’employé" value={form.emergency1Relation} onChange={set('emergency1Relation')} placeholder="Ex. Épouse, frère, ami" />
                    </Field>
                    <Field {...fp} k="emergency2Name" label="Contact 2 · nom" display={form.emergency2Name}>
                      <input className="ctl" data-field="emergency2Name" data-testid="emp-em2-name" aria-label="Contact d’urgence 2 — nom" value={form.emergency2Name} onChange={set('emergency2Name')} placeholder="Facultatif" />
                    </Field>
                    <Field {...fp} k="emergency2Phone" label="Contact 2 · téléphone" display={form.emergency2Phone ? <Mono>{form.emergency2Phone}</Mono> : ''}>
                      <input className="ctl" data-field="emergency2Phone" data-testid="emp-em2-phone" aria-label="Contact d’urgence 2 — téléphone" value={form.emergency2Phone} onChange={set('emergency2Phone')} placeholder="Facultatif" />
                    </Field>
                    <Field {...fp} k="emergency2Relation" label="Contact 2 · lien" display={form.emergency2Relation}>
                      <input className="ctl" data-field="emergency2Relation" data-testid="emp-em2-relation" aria-label="Contact d’urgence 2 — lien avec l’employé" value={form.emergency2Relation} onChange={set('emergency2Relation')} placeholder="Facultatif" />
                    </Field>
                  </div>
                </Section>
              )}

              {/* STATUT — danger zone (edit only, existing records only) */}
              {!isCreate && emp && canEditFull && (
                <div className="sec danger" ref={(el) => { secRefs.current.statut = el; }} data-testid="emp-section-Statut">
                  <div className="sec-h">
                    <span className="sec-ic"><AlertTriangle /></span>
                    <div><div className="sec-t">Statut</div><div className="sec-d">Archivage et fin de contrat</div></div>
                  </div>
                  <div className="danger-row">
                    <div className="tx">
                      <b>{emp.statsExcluded ? 'Réintégrer dans les statistiques' : 'Exclure des statistiques'}</b>
                      <p>{emp.statsExcluded
                        ? 'Actuellement exclu des KPI agrégés (vue d’ensemble de l’inspection). Réintègre l’employé dans tous les calculs d’équipe.'
                        : 'Pour la direction / les gérants qui ne badgent pas : l’employé sort de tous les KPI agrégés (vue d’ensemble de l’inspection). Sa fiche individuelle reste consultable.'}</p>
                    </div>
                    <button className="btn ghost" onClick={toggleStatsExcluded} disabled={update.isPending} data-testid="emp-toggle-stats">
                      <Flag /> {emp.statsExcluded ? 'Réintégrer' : 'Exclure des stats'}
                    </button>
                  </div>
                  <div className="danger-row">
                    <div className="tx">
                      <b>{emp.pointageExcluded ? 'Remettre dans le pointage' : 'Mettre hors pointage'}</b>
                      <p>{emp.pointageExcluded
                        ? 'Actuellement hors pointage : absent de la saisie quotidienne, de la grille mensuelle, des imports et des relances. Le réintégrer le fait réapparaître partout.'
                        : 'Pour qui ne badge pas du tout : disparaît de la saisie quotidienne, des blockers de validation, de la grille mensuelle, des lignes d’import pointeuse et des relances. Sa fiche, ses congés et sa paie ne changent pas.'}</p>
                    </div>
                    <button className="btn ghost" onClick={togglePointageExcluded} disabled={update.isPending} data-testid="emp-toggle-pointage">
                      <Flag /> {emp.pointageExcluded ? 'Remettre' : 'Hors pointage'}
                    </button>
                  </div>
                  <div className="danger-row">
                    <div className="tx">
                      <b>{emp.payrollExcluded ? 'Remettre dans la paie' : 'Mettre hors paie'}</b>
                      <p>{emp.payrollExcluded
                        ? 'Actuellement hors paie : aucune ligne de salaire, aucun bonus, aucun virement dans les fichiers banque. Le réintégrer le fait réapparaître dès le mois en cours.'
                        : 'Pour la direction / les associés non salariés : disparaît du module Salaires (lignes, bonus, fichiers banque) dès le mois en cours. Les mois déjà marqués envoyés gardent leur ligne.'}</p>
                    </div>
                    <button className="btn ghost" onClick={togglePayrollExcluded} disabled={update.isPending} data-testid="emp-toggle-payroll">
                      <Flag /> {emp.payrollExcluded ? 'Remettre' : 'Hors paie'}
                    </button>
                  </div>
                  <div className="danger-row">
                    <div className="tx">
                      <b>{emp.active ? 'Archiver l’employé' : 'Réactiver l’employé'}</b>
                      <p>{emp.active
                        ? 'Passe l’employé en inactif (active = false) et enregistre la date de sortie. Les pointages historiques sont conservés et restent consultables dans les rapports.'
                        : 'Réactive l’employé et efface la date de sortie.'}</p>
                    </div>
                    <button className="btn danger" onClick={toggleActive} disabled={deactivate.isPending || update.isPending} data-testid="emp-toggle-active">
                      <Archive /> {emp.active ? 'Archiver l’employé' : 'Réactiver l’employé'}
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ---------- SAVE BAR (flow child, edit only) ---------- */}
      {editing && (
        <div className="savebar">
          <span className={'savebar-dot' + (dirty ? '' : ' clean')} />
          <span className="savebar-tx">
            {dirty
              ? <><b>{dirtyCount} modification{dirtyCount > 1 ? 's' : ''}</b> non enregistrée{dirtyCount > 1 ? 's' : ''}</>
              : <>Mode édition — aucune modification</>}
          </span>
          <div className="savebar-sp" />
          <button className="btn ghost" onClick={cancel} data-testid="emp-cancel">Annuler</button>
          <button className="btn primary" onClick={save} disabled={!canSave || saving} data-testid="emp-save">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}

      {/* ---------- BALANCE MODAL ---------- */}
      {modal && emp && <AdjustModal emp={emp} themeDark={themeDark} onClose={() => setModal(false)} />}
    </div>
  );
}

function Section({ s, refCb, children }: { s: SectionDef; refCb: (el: HTMLElement | null) => void; children: React.ReactNode }) {
  const Icon = s.icon;
  return (
    <section className="sec" ref={refCb} data-testid={`emp-section-${s.t}`}>
      <div className="sec-h">
        <span className="sec-ic"><Icon /></span>
        <div><div className="sec-t">{s.t}</div><div className="sec-d">{s.d}</div></div>
      </div>
      {children}
    </section>
  );
}

// View mode → read-only value (click to edit). Edit mode → the control + hint.
function Field({
  editing, k, label, req, display, hint, full, onEdit, canEditField, children,
}: {
  editing: boolean;
  k: keyof DetailForm;
  label: string;
  req?: boolean;
  display: React.ReactNode;
  hint?: React.ReactNode;
  full?: boolean;
  onEdit: (k?: keyof DetailForm) => void;
  /** Whether this viewer may write THIS field (see fieldEditable). */
  canEditField: (k: keyof DetailForm) => boolean;
  children: React.ReactNode;
}) {
  const empty = display === '' || display == null || display === false;
  const writable = canEditField(k);
  // Read-only for this viewer: same value, no input, no pencil — an
  // affordance that leads to a 403 is worse than no affordance.
  if (!writable) {
    return (
      <div className={'f' + (full ? ' full' : '')}>
        <label className="f-l">{label}</label>
        <div className={'f-val is-locked' + (empty ? ' empty' : '')}>
          <span>{empty ? 'Non renseigné' : display}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={'f' + (full ? ' full' : '')}>
      <label className="f-l">{label}{editing && req && <span className="req">*</span>}</label>
      {editing ? (
        <>
          {children}
          {hint && (typeof hint === 'string' ? <span className="f-hint">{hint}</span> : hint)}
        </>
      ) : (
        <button type="button" className={'f-val' + (empty ? ' empty' : '')} onClick={() => onEdit(k)}>
          <span>{empty ? 'Non renseigné' : display}</span>
          <span className="edit-ic"><Pencil /></span>
        </button>
      )}
    </div>
  );
}

// Poste history list + add-row (edit-mode only, hidden via CSS in view).
function PosteSection({ emp }: { emp: EmployeeFullDTO }) {
  const history = useEmployeePostes(emp.id);
  const catalog = usePostes({ entityId: emp.entityId, activeOnly: true });
  const add = useAddEmployeePoste();
  const [posteId, setPosteId] = useState('');
  const [dateDebut, setDateDebut] = useState('');

  const rows: EmployeePosteDTO[] = history.data ?? [];
  const catalogEmpty = (catalog.data ?? []).length === 0;

  const submit = async () => {
    if (!posteId || !dateDebut) {
      toast.danger('Choisissez un poste et une date de début.');
      return;
    }
    try {
      await add.mutateAsync({ id: emp.id, posteId, dateDebut });
      toast.success('Poste ajouté.');
      setPosteId('');
      setDateDebut('');
    } catch {
      toast.danger("Échec de l'ajout du poste.");
    }
  };

  return (
    <>
      {rows.length > 0 ? (
        <div className="poste-list" data-testid="emp-poste-history">
          {rows.map((r, i) => (
            <div key={r.id} className={'poste-row' + (i === 0 ? ' current' : '')}>
              <span className="ic"><Briefcase /></span>
              <div>
                <div className="tx">{r.label}</div>
                <div className="sub">Depuis le {frDate(r.dateDebut)}</div>
              </div>
              {i === 0 ? <span className="badge">Actuel</span> : <span className="since mono">{frDate(r.dateDebut)}</span>}
            </div>
          ))}
        </div>
      ) : (
        <div className="poste-empty">
          <span className="ic"><Briefcase /></span>
          <div>
            <div className="tx">Aucun poste enregistré</div>
            <div className="sub">{catalogEmpty
              ? <>Le catalogue est vide — ajoutez des postes dans <a>Paramètres → Postes</a></>
              : 'Ajoutez une affectation ci-dessous.'}</div>
          </div>
        </div>
      )}
      <div className="poste-add">
        <div className="f">
          <label className="f-l">Poste</label>
          <select className="ctl" value={posteId} onChange={(e) => setPosteId(e.target.value)} disabled={catalogEmpty} data-testid="emp-poste-select" aria-label="Poste">
            <option value="">— Choisir un poste —</option>
            {(catalog.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.label}{p.entityId ? '' : ' (toutes entités)'}</option>
            ))}
          </select>
        </div>
        <div className="f">
          <label className="f-l">Depuis</label>
          <input className="ctl" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} disabled={catalogEmpty} data-testid="emp-poste-date" aria-label="Date de début du poste" />
        </div>
        <button className="btn primary" onClick={submit} disabled={catalogEmpty || add.isPending} data-testid="emp-poste-add">
          <Plus /> {add.isPending ? '…' : 'Ajouter'}
        </button>
      </div>
    </>
  );
}

// Reference-salary history — append-only, mirrors the poste history
// visually. Entries are stamped whenever the reference salary above is
// changed (here in the fiche); per-month overrides on Salaires & bonus
// do NOT append here. No add-row: editing the field above is the path.
function SalaryHistory({ emp }: { emp: EmployeeFullDTO }) {
  const history = useEmployeeSalaries(emp.id);
  const rows = history.data ?? [];

  return rows.length > 0 ? (
    <div className="poste-list" data-testid="emp-salary-history">
      {rows.map((r, i) => (
        <div key={r.id} className={'poste-row' + (i === 0 ? ' current' : '')}>
          <span className="ic"><Banknote /></span>
          <div>
            <div className="tx mono">{fmtDA(r.amount)} DA</div>
            <div className="sub">
              Depuis le {frDate(r.dateDebut)}{r.setByName ? ` · par ${r.setByName}` : ''}
            </div>
          </div>
          {i === 0 ? <span className="badge">Actuel</span> : <span className="since mono">{frDate(r.dateDebut)}</span>}
        </div>
      ))}
    </div>
  ) : (
    <div className="poste-empty">
      <span className="ic"><Banknote /></span>
      <div>
        <div className="tx">{(emp.salary ?? 0) > 0 ? <span className="mono">{fmtDA(emp.salary!)} DA</span> : 'Aucun salaire enregistré'}</div>
        <div className="sub">L’historique se remplit à chaque changement du salaire de référence.</div>
      </div>
    </div>
  );
}

// Adjust-balance modal — ledger-backed (MANUAL_ADDITION + required
// reason). Segmented Congé/Récup · ±0.5 stepper · live preview.
function AdjustModal({ emp, themeDark, onClose }: { emp: EmployeeFullDTO; themeDark: boolean; onClose: () => void }) {
  const adjust = useAdjustEmployeeBalance();
  const [type, setType] = useState<LeaveType>('CONGE');
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState('');

  const cur = type === 'CONGE' ? emp.congeBalance : emp.recupBalance;
  const valid = delta !== 0 && reason.trim().length > 0;

  const apply = async () => {
    if (!valid) {
      toast.danger('Renseignez une variation non nulle et un motif.');
      return;
    }
    try {
      await adjust.mutateAsync({ id: emp.id, type, amount: delta, reason: reason.trim() });
      toast.success('Solde ajusté.');
      onClose();
    } catch {
      toast.danger("Échec de l'ajustement.");
    }
  };

  return (
    <div className="emp-modal-bd" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'emp-modal' + (themeDark ? ' theme-dark' : '')}>
        <div className="emp-modal-h">
          <div className="emp-modal-t">Ajuster un solde</div>
          <button className="emp-modal-x" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <p className="emp-modal-sub">Créditez ou débitez manuellement les compteurs. Un motif est obligatoire pour la traçabilité.</p>
        <div className="emp-modal-grid">
          <div className="emp-seg">
            <button className={type === 'CONGE' ? 'on' : ''} onClick={() => setType('CONGE')} data-testid="emp-bal-type-conge">Congé</button>
            <button className={type === 'RECUP' ? 'on' : ''} onClick={() => setType('RECUP')} data-testid="emp-bal-type-recup">Récup</button>
          </div>
          <div>
            <label className="f-l" style={{ marginBottom: 6, display: 'block' }}>Variation (jours)</label>
            <div className="emp-step">
              <button onClick={() => setDelta((d) => +(d - 0.5).toFixed(1))} aria-label="Diminuer">−</button>
              <input
                className="mono"
                data-testid="emp-bal-amount"
                aria-label="Variation (jours)"
                value={delta > 0 ? '+' + delta : String(delta)}
                onChange={(e) => { const v = parseFloat(e.target.value.replace(',', '.')); if (!isNaN(v)) setDelta(v); }}
              />
              <button onClick={() => setDelta((d) => +(d + 0.5).toFixed(1))} aria-label="Augmenter">＋</button>
            </div>
          </div>
          <div className="f">
            <label className="f-l">Motif</label>
            <input className="ctl" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="emp-bal-reason" aria-label="Motif (obligatoire)" placeholder="Ex. Régularisation report 2025" />
          </div>
          <div className="emp-modal-prev">Nouveau solde : <b>{fr1(Math.max(0, cur + delta))}</b> jours {type === 'CONGE' ? 'congé' : 'récup'}</div>
        </div>
        <div className="emp-modal-foot">
          <button className="btn ghost" onClick={onClose}>Annuler</button>
          <button className="btn primary" onClick={apply} disabled={!valid || adjust.isPending} data-testid="emp-bal-submit">
            {adjust.isPending ? 'Ajustement…' : 'Appliquer'}
          </button>
        </div>
      </div>
    </div>
  );
}
